const inlineTextNamedEntities: Record<string, string> = {
  amp: "&",
  apos: "'",
  copy: "©",
  gt: ">",
  hellip: "…",
  lt: "<",
  mdash: "—",
  nbsp: "\u00a0",
  ndash: "–",
  quot: '"',
  reg: "®",
  trade: "™",
};

const inlineTagPattern = /<\/?(?:sup|sub|strong|b|em|i|u)>|<br\s*\/?>/gi;

export type InlineTextTag = "sup" | "sub" | "strong" | "em" | "u";

export type InlineTextToken =
  | {
      type: "text";
      value: string;
    }
  | {
      type: "break";
    }
  | {
      type: "open" | "close";
      raw: string;
      tag: InlineTextTag;
    };

function decodeCodePoint(value: string, radix: number): string | null {
  const codePoint = Number.parseInt(value, radix);

  if (
    !Number.isInteger(codePoint) ||
    codePoint < 0 ||
    codePoint > 0x10ffff ||
    (codePoint >= 0xd800 && codePoint <= 0xdfff)
  ) {
    return null;
  }

  return String.fromCodePoint(codePoint);
}

export function decodeInlineTextEntities(value: string): string {
  return value.replace(
    /&(?:#(\d+)|#x([0-9a-f]+)|([a-z][a-z0-9]+));/gi,
    (
      entity,
      decimal: string | undefined,
      hexadecimal: string | undefined,
      name: string | undefined,
    ) => {
      if (decimal) {
        return decodeCodePoint(decimal, 10) ?? entity;
      }

      if (hexadecimal) {
        return decodeCodePoint(hexadecimal, 16) ?? entity;
      }

      return name
        ? (inlineTextNamedEntities[name.toLowerCase()] ?? entity)
        : entity;
    },
  );
}

function normalizeTag(rawTag: string): InlineTextTag {
  const tag = rawTag.replace(/[</>]/g, "").toLowerCase();

  if (tag === "b") {
    return "strong";
  }

  if (tag === "i") {
    return "em";
  }

  return tag as InlineTextTag;
}

export function tokenizeInlineText(value: string): InlineTextToken[] {
  const tokens: InlineTextToken[] = [];
  let lastIndex = 0;

  for (const match of value.matchAll(inlineTagPattern)) {
    const index = match.index;

    if (index > lastIndex) {
      tokens.push({
        type: "text",
        value: decodeInlineTextEntities(value.slice(lastIndex, index)),
      });
    }

    const raw = match[0];
    const normalized = raw.toLowerCase();

    if (normalized.startsWith("<br")) {
      tokens.push({ type: "break" });
    } else {
      tokens.push({
        type: normalized.startsWith("</") ? "close" : "open",
        raw,
        tag: normalizeTag(raw),
      });
    }

    lastIndex = index + raw.length;
  }

  if (lastIndex < value.length) {
    tokens.push({
      type: "text",
      value: decodeInlineTextEntities(value.slice(lastIndex)),
    });
  }

  return tokens;
}

export function inlineTextToPlainText(
  value: null | string | undefined,
): string {
  if (!value) {
    return "";
  }

  return tokenizeInlineText(value)
    .map((token) => {
      if (token.type === "text") {
        return token.value;
      }

      if (token.type === "break") {
        return "\n";
      }

      return "";
    })
    .join("");
}
